<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0

@component
Displays the transcript of a file including the translation and a form to edit the speakers. Needs a SingleTranscript instance in the property "transcript"
-->

<script>
import Link from '../../components/Link.svelte';
import { TranscriptionJobsStore } from '../../libs/TranscriptionAPI/TranscriptionJobsStore.js';
import { getMicroserviceConfig } from '../../utils/MicroserviceConfigs';
import uiStore from '../../utils/UIStore';
import { copyWithDisclaimers } from '../../utils/copyHook.js';
import TranscriptMediaPlayer from './TranscriptMediaPlayer.svelte';

/**
 * @param - the SingleTranscript instance that should be rendered
 */
export let transcript;
export let job;
let refreshRevision = 0;
let transcriptRows = [];
let translationRows = [];
let hasSpeakers = false;
let transcriptionDisclaimerBefore = '';
let transcriptionDisclaimerAfter = '';
let translationDisclaimerBefore = '';
let translationDisclaimerAfter = '';

$: {
  refreshRevision;
  transcriptRows = transcript.getTranscriptRows();
  translationRows = transcript.getTranslationRows();
  hasSpeakers = Object.keys(transcript.getSpeakerMap()).length > 0;
}

async function loadDisclaimers() {
  const [
    fetchedTranscriptionDisclaimerBefore,
    fetchedTranscriptionDisclaimerAfter,
    fetchedTranslationDisclaimerBefore,
    fetchedTranslationDisclaimerAfter,
  ] = await Promise.all([
    getMicroserviceConfig('transcription', 'transcription_disclaimer_before'),
    getMicroserviceConfig('transcription', 'transcription_disclaimer_after'),
    getMicroserviceConfig('transcription', 'translation_disclaimer_before'),
    getMicroserviceConfig('transcription', 'translation_disclaimer_after'),
  ]);

  transcriptionDisclaimerBefore = fetchedTranscriptionDisclaimerBefore ?? '';
  transcriptionDisclaimerAfter = fetchedTranscriptionDisclaimerAfter ?? '';
  translationDisclaimerBefore = fetchedTranslationDisclaimerBefore ?? '';
  translationDisclaimerAfter = fetchedTranslationDisclaimerAfter ?? '';
}

loadDisclaimers();

/**
 * Event handler: user updated the speaker names in the form.
 * @param key - The key of the speaker
 * @param newName - The name the user gave to this speaker
 */
function updateSpeaker(key, newName) {
  transcript.speaker_map[key].name = newName;
  TranscriptionJobsStore.update((jobs) => {
    return jobs.map((job) => {
      if (job.transcription === transcript) {
        job.transcription = transcript;
      }
      return job;
    });
  });
  refreshRevision += 1;
}

function getSpeakerDisplayName(speakerId) {
  return transcript.getSpeakerLabel(speakerId);
}

function formatScore(score) {
  return Number.isFinite(score) ? Number(score).toFixed(2) : '0.00';
}

let mediaPlayerOpen = false;
let mediaTime = null;
let mediaVolume = 1;
let mediaMuted = false;
let mediaPlayerPosition = { x: 96, y: 120 };
let mediaPlayerSize = null;
let seekRequest = null;
let seekRequestId = 0;

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function isTimedPart(part) {
  return isFiniteNumber(part.start);
}

function seekToPart(part) {
  if (!isTimedPart(part)) return;

  mediaPlayerOpen = true;
  mediaTime = part.start;
  // Incrementing the id turns repeated clicks on the same timestamp into distinct seek events.
  seekRequestId += 1;
  seekRequest = { id: seekRequestId, time: part.start };
}

function handlePartKeydown(event, part) {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  event.preventDefault();
  seekToPart(part);
}

function handleMediaTimeChange(event) {
  mediaTime = event.detail.time;
}

function handleMediaVolumeChange(event) {
  mediaVolume = event.detail.volume;
  mediaMuted = event.detail.muted;
}

function handleMediaLayoutChange(event) {
  mediaPlayerPosition = event.detail.position;
  mediaPlayerSize = event.detail.size;
}

function openMediaPlayer() {
  seekRequest = null;
  mediaPlayerOpen = true;
}

function closeMediaPlayer() {
  seekRequest = null;
  mediaPlayerOpen = false;
}

function isActivePart(part, currentTime) {
  if (!isTimedPart(part) || !isFiniteNumber(currentTime)) return false;

  const end = isFiniteNumber(part.end) && part.end > part.start ? part.end : part.start + 0.5;
  return currentTime >= part.start && currentTime < end;
}

//load ui texts from ui Store
let uiTexts;
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});

$: mediaPlayerTexts = uiTexts?.transcription?.mediaPlayer ?? {};
</script>


<div
	class="transcript"
	use:copyWithDisclaimers={{
		before: transcriptionDisclaimerBefore,
		after: transcriptionDisclaimerAfter,
		excludeSelectors: ['.probability-score'],
		selectors: ['.original-text', '.translated-text'],
		disclaimersBySelector: [
			{
				selector: '.original-text',
				before: transcriptionDisclaimerBefore,
				after: transcriptionDisclaimerAfter,
			},
			{
				selector: '.translated-text',
				before: translationDisclaimerBefore,
				after: translationDisclaimerAfter,
			},
		],
	}}
>
  <div class="transcript-actions">
    <button type="button" class="secondary media-player-toggle" on:click={openMediaPlayer}>
      {mediaPlayerTexts.openMediaPlayer}
    </button>
  </div>

  {#if mediaPlayerOpen}
    <TranscriptMediaPlayer
      {job}
      {seekRequest}
      initialTime={mediaTime ?? 0}
      initialVolume={mediaVolume}
      initialMuted={mediaMuted}
      initialPosition={mediaPlayerPosition}
      initialSize={mediaPlayerSize}
      on:timechange={handleMediaTimeChange}
      on:volumechange={handleMediaVolumeChange}
      on:layoutchange={handleMediaLayoutChange}
      on:close={closeMediaPlayer}
    />
  {/if}

	{#if transcript.hasMatchedTerms() }
	<div class="grey-bg">
		<h3 class="transcript-section">{uiTexts.transcription.foundKeywords}</h3>
		<div class="chips speaker-chips">
			{#each Object.entries(transcript.getMatchedTerms()) as [kategorie, begriffe]}
				<strong>{kategorie}: </strong>
					{#each begriffe as begriff}
						<div class="chip-item">
							{begriff.Fachbegriff}
							<span
								class="probability-score">{begriff.Bedeutung} </span>
							&nbsp;({begriff.count})
						</div>
					{/each}
			{/each}
		</div>
	</div>
	{/if}
	{#if hasSpeakers}
		<div class="grey-bg">
			<h3 class="transcript-section">{uiTexts.transcription.speakers}</h3>
			<div class="chips">
				{#each Object.entries(transcript.getSpeakerMap()) as [key, speaker]}
					<div class="chip-item">
						<input
							type="text"
							value={transcript.speaker_map[key].name}
							disabled={!speaker.is_editing}
							on:change={(e) => updateSpeaker(key, e.target.value)}
							on:blur={() => {
								speaker.is_editing = false;
								refreshRevision += 1;
							}}
						/>
						<button on:click={() => speaker.is_editing = true}
								aria-label="{uiTexts.transcription.editSpeakersNames}">
							<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor"
								viewBox="0 0 256 256">
								<path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM92.69,208H48V163.31l88-88L180.69,120ZM192,108.68,147.31,64l24-24L216,84.68Z"></path>
							</svg>
						</button>
					</div>
				{/each}
			</div>
		</div>
	{/if}
	<div class="row">
		<div class="original-text">
			<h3>{uiTexts.transcription.transcriptedText}</h3>
      {#if transcriptionDisclaimerBefore}
				<div class="transcript-disclaimer">
					{transcriptionDisclaimerBefore}
				</div>
			{/if}
		</div>
		<!-- if translation; zeige translation -->
        {#if translationRows.length > 0}
		    <div class="translated-text">
					<h3>{uiTexts.transcription.translatedText}</h3>
					{#if translationDisclaimerBefore}
						<div class="transcript-disclaimer">
							{translationDisclaimerBefore}
						</div>
					{/if}
		    </div>
        {/if}
		<!-- /if Translation -->
	</div>
	{#each transcriptRows as row, index}
		<div class="row speaker-row">
			<div class="original-text">
				<div class="speaker">
					{#if row.speakerId || row.timeStamp}
						<p class="timeslot">
							{#if row.speakerId}
								<span data-speakerID={row.speakerId}>{getSpeakerDisplayName(row.speakerId)}</span>
							{/if}
							{#if row.timeStamp}
								{#if row.speakerId}&nbsp;{/if}
								[<span>{row.timeStamp}</span>]
							{/if}
						</p>
					{/if}
					<p class="text">
						{#each row.parts as part, partIndex}
							<span
								data-score={part.score}
								class="abschnitt media-subsegment"
								class:active-subsegment={isActivePart(part, mediaTime)}
								class:seekable-subsegment={isTimedPart(part)}
								role="button"
								tabindex="0"
								on:click={() => seekToPart(part)}
								on:keydown={(event) => handlePartKeydown(event, part)}
							>
								{part.text}<span class="probability-score">Score: {formatScore(part.score)}</span>
							</span>
							{#if partIndex < row.parts.length - 1}{' '}{/if}
						{/each}
					</p>
				</div>
			</div>
			{#if translationRows.length > 0}
				<div class="translated-text">
					<div class="speaker">
						{#if translationRows[index] && (translationRows[index].speakerId || translationRows[index].timeStamp)}
							<p class="timeslot">
								{#if translationRows[index].speakerId}
									<span data-speakerID={translationRows[index].speakerId}>
										{getSpeakerDisplayName(translationRows[index].speakerId)}
									</span>
								{/if}
								{#if translationRows[index].timeStamp}
									{#if translationRows[index].speakerId}&nbsp;{/if}
									[<span>{translationRows[index].timeStamp}</span>]
								{/if}
							</p>
						{/if}
						<p class="text">{translationRows[index]?.text ?? ''}</p>
					</div>
				</div>
			{/if}
		</div>
	{/each}
  {#if transcriptionDisclaimerAfter || (translationRows.length > 0 && translationDisclaimerAfter)}
    <div class="row disclaimer-row">
      <div class="original-text">
        {#if transcriptionDisclaimerAfter}
          <div class="transcript-disclaimer">
            {transcriptionDisclaimerAfter}
          </div>
        {/if}
      </div>
      {#if translationRows.length > 0}
        <div class="translated-text">
          {#if translationDisclaimerAfter}
            <div class="transcript-disclaimer">
              {translationDisclaimerAfter}
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/if}

    <div style="display: none; justify-content: flex-start; gap: 12px">
    <!-- <div style="display: flex; justify-content: flex-start; gap: 12px"> -->
		<Link to="/zusammenfassung" style="min-width: 0;">
			<button class="secondary" style="width: auto; white-space:nowrap"
			on:click={() => {
                console.log("Dummy Button; Funktionalität noch zu implementieren.")
                }
            }
			>In Zusammenfassung übertragen</button>
		</Link>

		<Link to="/recherche" style="min-width: 0;">
			<button class="secondary" style="width: auto; white-space:nowrap"
			on:click={() => {
                console.log("Dummy Button; Funktionalität noch zu implementieren.")
                }
            }
			>In Recherche übertragen</button>
		</Link>
    </div>
</div>


<style>
	.transcript{
		padding-top: 12px;
	}
	.row{
		display: flex;
		gap: 24px;
	}
	.speaker-row{
		align-items: stretch;
	}
	.disclaimer-row{
		align-items: flex-start;
	}
	.original-text,
	.translated-text{
		flex: 1;
		padding-top: 8px;
		text-align: left;
	}

	.original-text h3,
	.translated-text h3{
		margin-bottom: 12px;
		font-size: 1rem;
		border-bottom: 1px solid #e2e2e2;
	}
	.transcript-disclaimer{
		margin-bottom: 12px;
		color: #666;
		font-size: 0.9rem;
		font-style: italic;
		line-height: 1.5;
	}
	.chip-item{
		position: relative;
	}
	.chip-item,
	.chips {
		display: flex;
		font-size: 0.85rem;
	}
	.chips strong{
		margin-right: 8px;
		padding-left: 16px;
	}
	.chips strong:first-child{
		padding-left: 0;
	}
	.chip-item input{
		background-color: #fff;
		padding: 0;
		color: #000;
		font-size: 0.85rem;
		line-height: 0.85rem;
		margin: 0;
		width:auto
	}
	.chip-item input:disabled{
		background-color: transparent;
	}
	.grey-bg{
		background-color: var(--bg-color);
		padding: 12px;
		margin-bottom: 8px;
	}
	.grey-bg h3{
		font-size: .8em;
		margin: 0 0 12px;
		text-align: left;
		text-transform: none;
	}
	.speaker{
		text-align: left;
	}
	.speaker-row .speaker{
		display: flex;
		flex-direction: column;
		height: 100%;
	}
	.speaker p{
		margin: 0 0 12px;
		padding: 0;
	}
	.speaker-row .text{
		flex: 1;
		font-size: 1rem;
		font-weight: 500;
	}
	.speaker-row .timeslot{
		font-size: 1rem;
		font-weight: 700;
	}
	.speaker p.text > .abschnitt{
		position: relative;
	}
	.speaker p.text > .abschnitt:hover{
		background-color: #f2f2f2;
	}
	.probability-score{
		position: absolute;
		top:-2.5rem;
		left: 0;
		-webkit-user-select: none;
		user-select: none;
		background-color: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 6px rgba(0,0,0,0.3);
		padding: 2px 8px;
		border: 2px solid #f2f2f2;
		/*display: none;*/
		visibility: hidden;
		min-width: 90px;
		opacity: 0;
		transition: opacity 0.125s ease 0.25s,
				visibility 0s linear 0.25s;
	}
	.chips .chip-item:hover .probability-score,
	.speaker p.text > .abschnitt:hover .probability-score{
		/*display: block;*/
		opacity: 1;
		visibility: visible;
	}
	p.timeslot{
		margin: 0;
	}
	.chip-item button{
		padding:2px;
		margin-left: 0.5rem;
		background-color: transparent;
		border-radius: 25%;
	}
	.chip-item button svg{
		fill: #000;
	}
	.chip-item {
		padding:2px 8px;
		margin-right: 8px;
		border-radius: 20px;
		border: #2c2929 1px solid;
		font-size: 0.85rem;
	}
	.speaker-chips{
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}
	.speaker-chips .chip-item{
		flex: 0 0 240px;	/* change these */
		max-width: 240px;	/* to adjust speaker rename width */
		margin-right: 0;
		display: flex;
		align-items: center;
		box-sizing: border-box;
	}
	.speaker-chips .chip-item input{
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.speaker-chips .chip-item button{
		flex: 0 0 auto;
	}
	.chip-item button svg{
		width: 1rem;
		height: auto;
	}

	.transcript-actions{
		display: flex;
		justify-content: flex-start;
		margin-bottom: 12px;
	}
	.media-player-toggle{
		width: auto;
		margin: 0;
	}
	.media-subsegment{
		display: inline;
		color: inherit;
		font: inherit;
		font-weight: inherit;
		line-height: inherit;
		letter-spacing: inherit;
		text-align: inherit;
		text-transform: none;
		white-space: normal;
		background: transparent;
		border-radius: 2px;
		box-decoration-break: clone;
		-webkit-box-decoration-break: clone;
	}
	.media-subsegment.seekable-subsegment{
		cursor: pointer;
	}
	.media-subsegment:not(.seekable-subsegment){
		cursor: default;
	}
	.speaker p.text > .media-subsegment.active-subsegment,
	.speaker p.text > .media-subsegment.active-subsegment:hover{
		background-color: rgba(255, 230, 0, 0.65);
	}
	.media-subsegment:focus-visible{
		outline: 2px solid #000;
		outline-offset: 2px;
	}

</style>
